/**
 * DeepRestPlayer — controls for one ready Deep Rest session, driving the
 * shared app-level player (lib/restPlayer.js), so playback survives tab
 * changes and the MiniPlayer above the nav shows the same state.
 *
 * "Read the script" fetches the full text: every word is readable before or
 * after listening (spec §10). After a full listen, a one-tap feedback row
 * tunes the next session (slower/faster, music quieter/louder).
 */

import { useEffect, useState } from 'react';
import { Play, Pause, RotateCcw, RotateCw, Download, FileText, Loader2, Headphones, Check } from 'lucide-react';
import { getRestSession, submitRestFeedback } from '../lib/api';
import { useRestPlayer, fmtTime } from '../lib/restPlayer';
import { trackAuthed } from '../lib/analytics';
import { useNudge } from '../lib/hints';
import Nudge from './Nudge';

const PACE = [['slower', 'Slower'], ['right', 'Just right'], ['faster', 'Faster']];
const MUSIC = [['quieter', 'Quieter'], ['right', 'Right'], ['louder', 'Louder']];

export default function DeepRestPlayer({ session, autoPlay = false, compact = false, onFeedback }) {
  const p = useRestPlayer();
  const [script, setScript] = useState(null);
  const [scriptOpen, setScriptOpen] = useState(false);
  const [scriptLoading, setScriptLoading] = useState(false);
  const [fb, setFb] = useState({ pace: null, music: null });
  const [fbSent, setFbSent] = useState(!!session?.feedback);
  const [fbBusy, setFbBusy] = useState(false);

  const id = session?.id;
  const isCurrent = p?.session?.id === id;
  const playing = isCurrent && p.playing;
  const time = isCurrent ? p.time : 0;
  const duration = isCurrent && p.duration ? p.duration : (session?.duration_seconds || 0);

  useEffect(() => {
    if (autoPlay && session?.play_url && p) p.load(session, { autoplay: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPlay, id]);

  useEffect(() => { setFbSent(!!session?.feedback); setFb({ pace: null, music: null }); }, [id, session?.feedback]);

  const toggle = async () => {
    if (!p || !session?.play_url) return;
    if (!isCurrent) {
      await p.load(session);
      trackAuthed('rest_play', { kind: session.kind || 'reset', source: session.source, length: session.length_pref });
    }
    await p.toggle();
  };

  const loadScript = async () => {
    if (script) { setScriptOpen(o => !o); return; }
    setScriptLoading(true);
    try {
      const res = await getRestSession(id);
      setScript(res.session?.script || null);
      setScriptOpen(true);
    } catch { /* leave closed */ } finally {
      setScriptLoading(false);
    }
  };

  const sendFeedback = async () => {
    if (fbBusy || (!fb.pace && !fb.music)) return;
    setFbBusy(true);
    try {
      const res = await submitRestFeedback(id, { pace: fb.pace || undefined, music: fb.music || undefined });
      if (!res.error) { setFbSent(true); onFeedback?.(res.prefs); }
    } catch { /* keep the row */ } finally {
      setFbBusy(false);
    }
  };

  const pct = duration ? Math.min(100, (time / duration) * 100) : 0;
  const showFeedback = !!session && !fbSent && (p?.finishedId === id || (session.play_count || 0) > 0);
  const feedbackNudge = useNudge('rest_feedback', showFeedback);

  if (!session || !session.play_url) return null;

  return (
    <div className={`bg-slate-900/70 border border-indigo-900/40 rounded-lg ${compact ? 'p-3' : 'p-4'}`}>
      <div className="flex items-center gap-3">
        <button
          onClick={toggle}
          aria-label={playing ? 'Pause' : 'Play'}
          className="w-12 h-12 rounded-full bg-indigo-600 hover:bg-indigo-500 text-white flex items-center justify-center flex-shrink-0 transition-colors"
        >
          {playing ? <Pause size={20} /> : <Play size={20} className="ml-0.5" />}
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-200 truncate">{session.title || 'Deep Rest'}</p>
          <div className="mt-1.5 h-1.5 bg-slate-800 rounded-full overflow-hidden">
            <div className="h-full bg-indigo-500 rounded-full transition-[width]" style={{ width: `${pct}%` }} />
          </div>
          <div className="flex justify-between mt-1 text-[11px] text-slate-500">
            <span>{fmtTime(time)}</span>
            <span>{fmtTime(duration)}</span>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mt-3">
        <div className="flex items-center gap-1">
          <button onClick={() => isCurrent && p.skip(-15)} aria-label="Back 15 seconds" className="p-2 text-slate-400 hover:text-slate-200"><RotateCcw size={16} /></button>
          <button onClick={() => isCurrent && p.skip(15)} aria-label="Forward 15 seconds" className="p-2 text-slate-400 hover:text-slate-200"><RotateCw size={16} /></button>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <button onClick={loadScript} className="text-slate-400 hover:text-slate-200 flex items-center gap-1">
            {scriptLoading ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />} {scriptOpen ? 'Hide script' : 'Read the script'}
          </button>
          {session.download_url && (
            <a href={session.download_url} className="text-slate-400 hover:text-slate-200 flex items-center gap-1" rel="noreferrer">
              <Download size={12} /> Download
            </a>
          )}
        </div>
      </div>

      {!compact && (
        <p className="mt-3 text-[11px] text-slate-500 flex items-center gap-1.5">
          <Headphones size={12} /> Headphones recommended. Not for use while driving or operating machinery.
        </p>
      )}

      {isCurrent && p.error && <p className="mt-2 text-xs text-amber-300">{p.error}</p>}

      {showFeedback && (
        <div className="mt-3 border-t border-slate-800 pt-3">
          <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">Tune the next one</p>
          <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
            <span className="text-xs text-slate-400 w-14">Pace</span>
            {PACE.map(([k, label]) => (
              <button key={k} onClick={() => setFb(f => ({ ...f, pace: k }))}
                className={`text-xs px-2.5 py-1 rounded-full border ${fb.pace === k ? 'border-indigo-500 bg-indigo-900/40 text-indigo-200' : 'border-slate-700 text-slate-400 hover:text-slate-200'}`}>
                {label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs text-slate-400 w-14">Music</span>
            {MUSIC.map(([k, label]) => (
              <button key={k} onClick={() => setFb(f => ({ ...f, music: k }))}
                className={`text-xs px-2.5 py-1 rounded-full border ${fb.music === k ? 'border-indigo-500 bg-indigo-900/40 text-indigo-200' : 'border-slate-700 text-slate-400 hover:text-slate-200'}`}>
                {label}
              </button>
            ))}
            <button onClick={sendFeedback} disabled={fbBusy || (!fb.pace && !fb.music)}
              className="ml-auto text-xs px-3 py-1 rounded-md bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-700 text-white flex items-center gap-1">
              {fbBusy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Save
            </button>
          </div>
        </div>
      )}
      {fbSent && !compact && <p className="mt-2 text-[11px] text-slate-500">Thanks. The next session uses this.</p>}
      {feedbackNudge.show && (
        <Nudge title="Two taps tune the next one." text="Slower or faster, music quieter or louder. Each answer nudges every session after this one." onDismiss={feedbackNudge.dismiss} />
      )}

      {scriptOpen && script && (
        <div className="mt-3 max-h-72 overflow-y-auto space-y-3 text-sm text-slate-300 leading-relaxed border-t border-slate-800 pt-3">
          {(script.segments || []).map((seg) => (
            <div key={seg.name}>
              <p className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">{seg.name}</p>
              {(seg.paragraphs || []).map((par, i) => <p key={i} className="mb-2">{par.text}</p>)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
